import { useEffect, useState } from "react";
import {
  ArrowLeft,
  Download,
  Share2,
  Copy,
  Check,
  FileText,
  Ban,
  RotateCcw,
  Trash2,
  ArrowRight,
} from "lucide-react";
import {
  type DocumentRecord,
  money,
  dateLabel,
  kindLabels,
  statusLabel,
  londonToday,
  totals,
} from "../../shared/domain";
import { makePdf, pdfFile, downloadPdf } from "../pdf";
import { api, errorMessage } from "../api";
import { Field, Modal, Notice, Spinner, AmountInput } from "./Common";
import type { Payment } from "../../shared/overview";
export function DocumentDetail({
  doc,
  onBack,
  onChange,
  onEdit,
  backLabel = "Invoices",
}: {
  doc: DocumentRecord;
  onBack: () => void;
  onChange: (d: DocumentRecord) => void;
  onEdit: (d: DocumentRecord) => void;
  backLabel?: string;
}) {
  const [busy, setBusy] = useState(""),
    [error, setError] = useState(""),
    [dialog, setDialog] = useState<"paid" | "void" | "trash" | null>(null),
    [date, setDate] = useState(londonToday()),
    [amount, setAmount] = useState(doc.totalPence - doc.paidPence),
    [payments, setPayments] = useState<Payment[]>([]),
    [paymentsError, setPaymentsError] = useState(""),
    [paymentsLoading, setPaymentsLoading] = useState(true),
    [paymentsAttempt, setPaymentsAttempt] = useState(0),
    [reason, setReason] = useState(""),
    [file, setFile] = useState<File | null>(null),
    [pdfError, setPdfError] = useState(""),
    [pdfAttempt, setPdfAttempt] = useState(0),
    [activity, setActivity] = useState<
      { action: string; actor: string; created_at: string; detail: string }[]
    >([]);
  useEffect(() => {
    let cancelled = false;
    setPaymentsLoading(true);
    setPaymentsError("");
    if (doc.kind === "invoice" && doc.number)
      void api<Payment[]>(`/documents/${doc.id}/payments`)
        .then((p) => {
          if (!cancelled) {
            setPayments(p);
            setPaymentsError("");
          }
        })
        .catch((e) => {
          if (!cancelled) setPaymentsError(errorMessage(e));
        })
        .finally(() => {
          if (!cancelled) setPaymentsLoading(false);
        });
    return () => {
      cancelled = true;
    };
  }, [doc.id, doc.version, doc.kind, doc.number, paymentsAttempt]);
  useEffect(() => {
    let cancelled = false;
    void api<typeof activity>(`/documents/${doc.id}/activity`)
      .then((a) => {
        if (!cancelled) setActivity(a);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [doc.id, doc.version, doc.pdfReady, doc.number]);
  useEffect(() => {
    let cancelled = false;
    setFile(null);
    setPdfError("");
    if (doc.pdfReady && doc.snapshot) {
      void makePdf(doc.snapshot)
        .then((result) => {
          if (!cancelled) setFile(pdfFile(result.bytes, `${doc.number}.pdf`));
        })
        .catch((e) => {
          if (!cancelled) setPdfError(errorMessage(e));
        });
    }
    return () => {
      cancelled = true;
    };
    // Issued snapshots are immutable. A payment update never invalidates this file.
  }, [doc.id, doc.pdfReady, doc.number, pdfAttempt]);
  async function action(name: string, body: unknown, edit = false) {
    setBusy(name);
    setError("");
    try {
      const d = await api<DocumentRecord>(
        `/documents/${doc.id}/${name}`,
        "POST",
        body,
      );
      setDialog(null);
      edit ? onEdit(d) : onChange(d);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy("");
    }
  }
  const summary = doc.snapshot,
    lines = summary?.draft.lines || doc.draft.lines,
    t = totals(summary?.draft || doc.draft),
    label = statusLabel(doc);
  return (
    <div className="page-narrow">
      <button className="back" onClick={onBack}>
        <ArrowLeft size={18} />
        {backLabel}
      </button>
      <div className="page-title detail-title">
        <div>
          <h1>{doc.clientName}</h1>
          <p className="document-number">{doc.number || "Unnumbered draft"}</p>
        </div>
        <span className={`badge ${label.toLowerCase().replaceAll(" ", "-")}`}>
          {label}
        </span>
      </div>
      {error && <Notice type="error">{error}</Notice>}
      {doc.status === "issuing" && (
        <Notice>
          This number is reserved. Finish issuing the document to export it.
          <button
            className="primary"
            disabled={!!busy}
            onClick={() =>
              void action("issue", { version: doc.version, token: "" })
            }
          >
            {busy ? <Spinner /> : <RotateCcw size={18} />}Finish issuance
          </button>
        </Notice>
      )}
      {doc.status === "void" && (
        <Notice>
          This document is void. Its original issued details are kept for your
          records.
        </Notice>
      )}
      <div className="document-total">
        <p>
          {doc.status === "paid"
            ? "Paid in full"
            : doc.kind === "invoice"
              ? "Amount outstanding"
              : "Document total"}
        </p>
        <strong>
          {money(
            doc.status === "paid"
              ? doc.totalPence
              : doc.kind === "invoice"
                ? Math.max(0, doc.totalPence - doc.paidPence)
                : doc.totalPence,
          )}
        </strong>
        <span>
          {doc.status === "paid" && doc.paymentDate
            ? `Received ${dateLabel(doc.paymentDate)}`
            : `Due ${dateLabel(doc.dueDate)}`}
        </span>
      </div>
      {doc.pdfReady && (
        <div className="primary-actions">
          {!file ? (
            <div className="pdf-preparation" role="status">
              {pdfError ? (
                <>
                  <Notice type="error">{pdfError}</Notice>
                  <button
                    className="secondary"
                    onClick={() => setPdfAttempt((n) => n + 1)}
                  >
                    Retry PDF
                  </button>
                </>
              ) : (
                <>
                  <button className="primary" disabled>
                    <Spinner />
                    Preparing PDF…
                  </button>
                  <span>
                    Preparing on this device. Ready to share in a moment.
                  </span>
                </>
              )}
            </div>
          ) : (
            <>
              <button
                className="primary"
                onClick={async () => {
                  try {
                    if (navigator.canShare?.({ files: [file] }))
                      await navigator.share({
                        files: [file],
                        title: doc.number || "Invoice",
                      });
                    else downloadPdf(file);
                  } catch (e) {
                    if (!(e instanceof Error && e.name === "AbortError"))
                      setError(errorMessage(e));
                  }
                }}
              >
                <Share2 size={18} />
                Share PDF
              </button>
              <button
                className="secondary"
                onClick={() => {
                  const url = URL.createObjectURL(file);
                  window.open(url, "_blank", "noopener");
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                }}
              >
                <FileText size={18} />
                Open PDF
              </button>
              <button
                className="icon-button"
                aria-label="Download PDF"
                onClick={() => downloadPdf(file)}
              >
                <Download size={20} />
              </button>
            </>
          )}
        </div>
      )}
      {doc.status === "draft" && (
        <button className="primary" onClick={() => onEdit(doc)}>
          <ArrowRight size={18} />
          Continue editing
        </button>
      )}
      <section className="settings-section">
        <h2>Document details</h2>
        <div className="detail-pairs">
          <span>Issue date</span>
          <strong>{dateLabel(doc.issueDate)}</strong>
          <span>{doc.kind === "quotation" ? "Valid until" : "Due date"}</span>
          <strong>{dateLabel(doc.dueDate)}</strong>
          {doc.draft.customerReference && (
            <>
              <span>Customer reference</span>
              <strong>{doc.draft.customerReference}</strong>
            </>
          )}
        </div>
        {summary && (
          <p className="muted">
            {[
              summary.client.billingAddress.line1,
              summary.client.billingAddress.line2,
              summary.client.billingAddress.city,
              summary.client.billingAddress.postcode,
            ]
              .filter(Boolean)
              .join(", ")}
          </p>
        )}
      </section>
      <section className="settings-section">
        <h2>Services</h2>
        {lines.map((l, i) => (
          <div className="detail-service" key={l.id}>
            <div>
              <small>
                Service {i + 1} · {dateLabel(l.supplyDate)}
              </small>
              <strong>{l.description}</strong>
            </div>
            <span>{money(l.netPence)}</span>
          </div>
        ))}
        <div className="totals">
          <div>
            <span>Subtotal</span>
            <span>{money(t.netPence)}</span>
          </div>
          <div>
            <span>VAT ({doc.draft.vatBasisPoints / 100}%)</span>
            <span>{money(t.vatPence)}</span>
          </div>
          <div className="grand-total">
            <strong>Total</strong>
            <strong>{money(doc.totalPence)}</strong>
          </div>
          {doc.paidPence > 0 && (
            <div>
              <span>Payment received</span>
              <span>{money(doc.paidPence)}</span>
            </div>
          )}
        </div>
      </section>
      {doc.kind === "invoice" && doc.paidPence > 0 && (
        <section className="settings-section payment-history">
          <h2>Payments received</h2>
          {paymentsLoading && (
            <p className="report-loading" role="status">
              <Spinner />
              {payments.length ? "Updating payments…" : "Loading payments…"}
            </p>
          )}
          {paymentsError && (
            <Notice type="error">
              {paymentsError}
              <button
                className="text-button"
                disabled={paymentsLoading}
                onClick={() => setPaymentsAttempt((n) => n + 1)}
              >
                Try payment history again
              </button>
            </Notice>
          )}
          {payments.map((p) => (
            <div className="payment-history-row" key={p.id}>
              <span>
                {p.receivedDate
                  ? dateLabel(p.receivedDate)
                  : "Date not recorded"}
                {p.source === "opening" && <small>Recorded when issued</small>}
              </span>
              <strong>{money(p.amountPence)}</strong>
            </div>
          ))}
        </section>
      )}
      {doc.draft.internalNote && (
        <section className="settings-section">
          <h2>Internal note</h2>
          <p className="preserve-lines">{doc.draft.internalNote}</p>
        </section>
      )}
      <div className="document-actions">
        {doc.status === "issued" && doc.kind === "invoice" && (
          <button
            className="secondary"
            onClick={() => {
              setAmount(doc.totalPence - doc.paidPence);
              setError("");
              setDialog("paid");
            }}
          >
            <Check size={18} />
            Record payment
          </button>
        )}
        {doc.kind !== "invoice" && doc.status === "issued" && (
          <button
            className="secondary"
            disabled={!!busy}
            onClick={() => void action("duplicate", { convert: true }, true)}
          >
            <ArrowRight size={18} />
            Create invoice
          </button>
        )}
        <button
          className="secondary"
          disabled={!!busy}
          onClick={() => void action("duplicate", {}, true)}
        >
          <Copy size={18} />
          Duplicate as draft
        </button>
        {doc.status === "issued" && (
          <button
            className="text-button danger-text"
            onClick={() => setDialog("void")}
          >
            <Ban size={17} />
            Void document
          </button>
        )}
        {doc.status === "draft" && (
          <button
            className="text-button danger-text"
            onClick={() => setDialog("trash")}
          >
            <Trash2 size={17} />
            Move to trash
          </button>
        )}
        {doc.status === "deleted" && (
          <button
            className="secondary"
            onClick={() =>
              void action("trash", { version: doc.version, restore: true })
            }
          >
            <RotateCcw size={18} />
            Restore draft
          </button>
        )}
      </div>
      <details className="activity">
        <summary>Activity</summary>
        {activity.length ? (
          activity.map((a, i) => (
            <div key={i}>
              <strong>
                {a.action.replaceAll(".", " ").replaceAll("_", " ")}
              </strong>
              <small>
                {new Date(a.created_at).toLocaleString("en-GB")} · {a.actor}
              </small>
              {a.action === "document.voided" && (
                <p>{JSON.parse(a.detail).reason}</p>
              )}
            </div>
          ))
        ) : (
          <p className="muted">No activity recorded.</p>
        )}
      </details>
      {dialog && (
        <Modal
          title={
            dialog === "paid"
              ? "Record payment"
              : dialog === "void"
                ? "Void this document?"
                : "Move draft to trash?"
          }
          onClose={() => setDialog(null)}
          sheet={dialog === "paid"}
          dismissible={!busy}
        >
          {error && <Notice type="error">{error}</Notice>}
          {dialog === "paid" ? (
            <>
              <p>
                {money(doc.totalPence - doc.paidPence)} outstanding on{" "}
                {doc.number}. Record a deposit or the full remaining balance.
              </p>
              <Field
                label="Amount received"
                required
                error={
                  amount > doc.totalPence - doc.paidPence
                    ? "This exceeds the outstanding balance."
                    : undefined
                }
                hint="Including VAT"
              >
                <AmountInput value={amount} onChange={setAmount} required />
              </Field>
              <Field label="Payment received date">
                <input
                  type="date"
                  value={date}
                  max={londonToday()}
                  onChange={(e) => setDate(e.target.value)}
                />
              </Field>
            </>
          ) : dialog === "void" ? (
            <>
              <p>
                The number {doc.number} will remain reserved and its issued
                details will be kept for future PDF exports. You can duplicate
                it to prepare a replacement.
              </p>
              <Field label="Reason" required>
                <textarea
                  value={reason}
                  maxLength={500}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                />
              </Field>
            </>
          ) : (
            <p>You can restore this draft from the Trash filter.</p>
          )}
          <div className="dialog-actions">
            <button className="secondary" onClick={() => setDialog(null)}>
              Cancel
            </button>
            <button
              className={dialog === "paid" ? "primary" : "danger"}
              disabled={
                !!busy ||
                (dialog === "void" && !reason.trim()) ||
                (dialog === "paid" &&
                  (!date ||
                    date > londonToday() ||
                    amount <= 0 ||
                    amount > doc.totalPence - doc.paidPence))
              }
              onClick={() =>
                void action(dialog, {
                  version: doc.version,
                  date,
                  reason,
                  ...(dialog === "paid" ? { amountPence: amount } : {}),
                })
              }
            >
              {busy && <Spinner />}
              {dialog === "paid"
                ? "Confirm payment"
                : dialog === "void"
                  ? "Void document"
                  : "Move to trash"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
